import {
  REVIEW_COMPLETION_NOTIFICATIONS_KEY,
  onSessionRemoved,
} from "@/shared/lib/token/token"

export const COMPLETION_ALERT = "completion-alert"
export const REVIEW_COMPLETION_CHANGE = "review-completion-change"

export type CompletionAlert = {
  kind: "assistant" | "cat-review" | "review"
  resourceId: string
  runId: string
}

export type WatchedReview = Omit<CompletionAlert, "kind"> & {
  kind: "cat-review" | "review"
  startedAt: string
}
type State = {
  watching: WatchedReview[]
  unread: CompletionAlert[]
  settled: WatchedReview[]
}
const empty = (): State => ({ watching: [], unread: [], settled: [] })
let fallback = empty()
let storageDirty = false

export const completionKey = (entry: CompletionAlert) =>
  JSON.stringify([entry.kind, entry.resourceId, entry.runId])

const validAlert = (entry: unknown): entry is CompletionAlert => {
  if (!entry || typeof entry !== "object") return false
  const value = entry as Partial<CompletionAlert>
  return (
    (value.kind === "cat-review" || value.kind === "review") &&
    typeof value.resourceId === "string" &&
    typeof value.runId === "string"
  )
}

const validWatch = (entry: unknown): entry is WatchedReview =>
  validAlert(entry) &&
  "startedAt" in entry &&
  typeof entry.startedAt === "string" &&
  Number.isFinite(Date.parse(entry.startedAt))

const read = (): State => {
  if (storageDirty) return fallback
  try {
    const raw = JSON.parse(
      sessionStorage.getItem(REVIEW_COMPLETION_NOTIFICATIONS_KEY) ?? "null"
    ) as Partial<State> | null
    fallback = {
      watching: Array.isArray(raw?.watching)
        ? raw.watching.filter(validWatch)
        : [],
      unread: Array.isArray(raw?.unread) ? raw.unread.filter(validAlert) : [],
      settled: Array.isArray(raw?.settled)
        ? raw.settled.filter(validWatch)
        : [],
    }
  } catch {
    storageDirty = true
  }
  return fallback
}

const publish = (state: State) => {
  fallback = state
  try {
    sessionStorage.setItem(
      REVIEW_COMPLETION_NOTIFICATIONS_KEY,
      JSON.stringify(state)
    )
    storageDirty = false
  } catch {
    storageDirty = true
  }
  window.dispatchEvent(new Event(REVIEW_COMPLETION_CHANGE))
}

/** Only identifiers and the server's start timestamp, never document names or content. */
export const watchReviewCompletion = (entry: WatchedReview) => {
  const state = read()
  const key = completionKey(entry)
  if (
    state.settled.some(
      (item) =>
        completionKey(item) === key && item.startedAt === entry.startedAt
    )
  )
    return
  const previous = state.watching.find((item) => completionKey(item) === key)
  if (
    previous &&
    Date.parse(previous.startedAt) >= Date.parse(entry.startedAt)
  )
    return
  // A target ID survives retries. Replace its previous claim so an outstanding poll for that
  // claim cannot retire the retry, and remember it so a late cached snapshot cannot re-arm it.
  publish({
    ...state,
    watching: [
      ...state.watching.filter((item) => completionKey(item) !== key),
      entry,
    ],
    settled: previous ? [...state.settled, previous] : state.settled,
  })
}

export const watchedReviewCompletions = () => read().watching
export const unreadReviewCompletions = () => read().unread

export const forgetReviewCompletion = (entry: WatchedReview) => {
  const state = read()
  if (
    !state.watching.some(
      (item) =>
        completionKey(item) === completionKey(entry) &&
        item.startedAt === entry.startedAt
    )
  )
    return
  publish({
    ...state,
    settled: [...state.settled, entry],
    watching: state.watching.filter(
      (item) =>
        completionKey(item) !== completionKey(entry) ||
        item.startedAt !== entry.startedAt
    ),
  })
}

export const emitCompletionAlert = (entry: CompletionAlert) =>
  window.dispatchEvent(new CustomEvent(COMPLETION_ALERT, { detail: entry }))

/** A deleted resource, replaced watch, logout or another observer wins over a late response. */
export const finishReviewCompletion = (
  entry: WatchedReview,
  unread: boolean
) => {
  const state = read()
  if (
    !state.watching.some(
      (item) =>
        completionKey(item) === completionKey(entry) &&
        item.startedAt === entry.startedAt
    )
  )
    return false
  const alert: CompletionAlert = {
    kind: entry.kind,
    resourceId: entry.resourceId,
    runId: entry.runId,
  }
  publish({
    watching: state.watching.filter(
      (item) => completionKey(item) !== completionKey(entry)
    ),
    unread: unread
      ? [
          ...state.unread.filter(
            (item) => completionKey(item) !== completionKey(alert)
          ),
          alert,
        ]
      : state.unread,
    settled: [...state.settled, entry],
  })
  emitCompletionAlert(alert)
  return true
}

export const clearReviewResourceCompletions = (
  kind: CompletionAlert["kind"],
  resourceId: string
) => {
  const state = read()
  const keep = (entry: CompletionAlert) =>
    entry.kind !== kind || entry.resourceId !== resourceId
  publish({
    watching: state.watching.filter(keep),
    unread: state.unread.filter(keep),
    settled: state.settled.filter(keep),
  })
}

export const acknowledgeReviewCompletions = (pathname: string) => {
  const state = read()
  const unread = state.unread.filter(
    (entry) => completionPath(entry) !== pathname.replace(/\/$/, "")
  )
  if (unread.length !== state.unread.length) publish({ ...state, unread })
}

export const completionPath = (entry: CompletionAlert) => {
  if (entry.kind === "assistant") return `/assistant/${entry.resourceId}`
  if (entry.kind === "review") return `/reviews/${entry.resourceId}`
  return `/translations/jobs/${entry.resourceId}/editor`
}

export const isAwayFromCompletion = (entry: CompletionAlert) =>
  document.visibilityState === "hidden" ||
  !document.hasFocus() ||
  window.location.pathname.replace(/\/$/, "") !== completionPath(entry)

export const clearReviewCompletions = () => {
  fallback = empty()
  try {
    sessionStorage.removeItem(REVIEW_COMPLETION_NOTIFICATIONS_KEY)
    storageDirty = false
  } catch {
    storageDirty = true
  }
  window.dispatchEvent(new Event(REVIEW_COMPLETION_CHANGE))
}

onSessionRemoved(clearReviewCompletions)
